import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideAppInitializer,
  inject,
  isDevMode,
} from '@angular/core';
import { provideRouter, TitleStrategy } from '@angular/router';
import { registerLocaleData } from '@angular/common';
import localeId from '@angular/common/locales/id';

import { routes } from './app.routes';

// Daftarkan data locale Indonesia agar DatePipe/`| date` bisa memakai 'id' (app bilingual).
registerLocaleData(localeId);
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { TranslocoHttpLoader } from './transloco-loader';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { CookieService } from '@shared/services/cookie.service';
import { LOCALE_COOKIE } from '@shared/constants/i18n.constants';
import { I18nTitleStrategy } from '@shared/services/i18n-title.strategy';
import {
  authInterceptor,
  credentialsInterceptor,
  menuPathInterceptor,
  xsrfInterceptor,
} from '@app/core';
import { MAT_ICON_DEFAULT_OPTIONS } from '@angular/material/icon';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    { provide: TitleStrategy, useClass: I18nTitleStrategy },
    /*
      Ikon rounded mengikuti Procura (DESIGN_SYSTEM.md memakai Material Symbols Rounded).
      Yang dipakai di sini adalah varian Round dari paket `material-icons` yang sudah terpasang
      (173 KB) — bukan paket `material-symbols` (5,3 MB, dan `font-display: block` membuat SELURUH
      ikon kosong sampai font selesai diunduh). Bentuknya setara; seluruh 106 nama ikon yang
      dipakai aplikasi ini sudah diverifikasi ada di set Round.

      Di-set global agar setiap `<mat-icon>` ikut tanpa perlu `fontSet` per pemakaian; kelasnya
      berasal dari `material-icons/iconfont/round.css` (lihat `angular.json`).
    */
    { provide: MAT_ICON_DEFAULT_OPTIONS, useValue: { fontSet: 'material-icons-round' } },
    // Urutan disengaja: request mengalir auth → credentials → xsrf. Saat authInterceptor
    // mengulang request setelah refresh, aliran melewati credentials & xsrf lagi → cookie
    // ikut terkirim dan token XSRF yang baru dirotasi terpungut. `menuPathInterceptor` paling
    // belakang karena alasan yang sama: request ulang tetap membawa `X-Menu-Path`.
    provideHttpClient(
      withInterceptors([
        authInterceptor,
        credentialsInterceptor,
        xsrfInterceptor,
        menuPathInterceptor,
      ]),
    ),
    provideTransloco({
      config: {
        availableLangs: ['en', 'id'],
        defaultLang: 'en',
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    /*
      Pulihkan bahasa pilihan user SEBELUM komponen pertama dirender.

      `defaultLang: 'en'` di atas adalah bahasa AWAL, bukan bahasa tersimpan — Transloco tidak
      punya persistensi bawaan (itu paket terpisah, `transloco-persist-lang`, yang tidak dipasang
      di sini). Tanpa initializer ini, tiap refresh memulai sesi dari 'en' sementara switcher
      bahasa cuma memulihkan LABEL dropdown-nya dari cookie — jadi dropdown menulis "Indonesia"
      di atas halaman yang seluruhnya berbahasa Inggris.

      Ditaruh di bootstrap, bukan di layout, karena inilah satu-satunya titik yang dilewati SEMUA
      rute: auth-layout dulu menyinkronkan sendiri, main-layout tidak, sehingga bug-nya cuma
      terlihat setelah login.
    */
    provideAppInitializer(() => {
      const transloco = inject(TranslocoService);
      const saved = inject(CookieService).getCookie(LOCALE_COOKIE);

      // `isLang` menjaga dari cookie usang/karangan (mis. 'fr' setelah dihapus dari availableLangs)
      // — `setActiveLang` menerima apa saja, lalu loader-nya 404 dan SEMUA teks jadi key mentah.
      if (!saved || !transloco.isLang(saved) || saved === transloco.getActiveLang()) return;

      transloco.setActiveLang(saved);
      // Kembalikan Observable-nya supaya bootstrap MENUNGGU file terjemahannya. Kalau tidak,
      // render pertama sempat memakai 'en' lalu berkedip ke 'id' saat file-nya tiba.
      return transloco.load(saved);
    }),
  ],
};
